import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageShell, { SectionTitle } from './PageShell';
import { ExternalLink, ArrowRight, Bike, CheckCircle2, AlertTriangle, Clock, GraduationCap, Shield } from 'lucide-react';

const C2R = 'https://code2roues.fr/?utm_source=codevoiture&utm_medium=seo&utm_campaign=permis-125';

const conditions = [
  { icon: Clock, t: 'Permis B depuis 2 ans minimum', d: "Il faut être titulaire du permis B depuis au moins 2 ans à la date de début de la formation." },
  { icon: GraduationCap, t: 'Formation de 7 heures', d: "2 h de théorie, 2 h hors circulation (plateau) et 3 h en circulation, dans une école de conduite agréée. Aucun examen final." },
  { icon: Shield, t: 'Attestation à vie', d: "L'attestation de suivi de formation est valable à vie et doit être présentée avec le permis B en cas de contrôle." },
];

const machines = [
  { k: 'Motocyclette légère', v: '125 cm³ max · 11 kW (15 ch) max' },
  { k: 'Scooter 3 roues (L5e)', v: 'Puissance ≤ 15 kW avec la formation 7 h' },
  { k: 'Rapport poids/puissance', v: '≤ 0,1 kW/kg' },
];

const dispenses = [
  "Vous avez obtenu votre permis B avant le 1er mars 1980",
  "Vous avez déjà conduit une 125 assurée entre 2006 et 2010 (justificatif d'assurance)",
  "Vous êtes titulaire d'un permis A1, A2 ou A",
];

export default function Permis125Page() {
  useEffect(() => {
    document.title = 'Conduire une 125 avec le permis B — Formation 7 h | CodeVoiture.fr';
    const meta = document.querySelector('meta[name="description"]');
    const prev = meta?.getAttribute('content');
    meta?.setAttribute('content', "Permis B depuis 2 ans ? Vous pouvez conduire une moto 125 cm³ ou un scooter 3 roues après une formation de 7 heures, sans examen. Conditions, dispenses, équipement obligatoire — guide ECSR complet.");
    return () => { document.title = 'CodeVoiture.fr — Réussir le Code et la conduite (gratuit, ECSR)'; if (prev) meta?.setAttribute('content', prev); };
  }, []);

  return (
    <PageShell>
      <section className="max-w-5xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12" data-testid="permis-125-page">
        <SectionTitle
          kicker="Passerelle voiture → 2 roues"
          title="Conduire une 125 avec le permis B : la formation 7 heures"
          sub="Vous avez le permis voiture depuis 2 ans ? Une simple formation de 7 heures — sans examen — vous ouvre les motos légères 125 cm³ et les scooters 3 roues. Le guide complet par des moniteurs ECSR."
        />

        {/* Conditions */}
        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          {conditions.map((c, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5" data-testid={`permis-125-condition-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-400/25 flex items-center justify-center mb-3">
                <c.icon size={18} className="text-sky-300" />
              </div>
              <div className="text-white font-semibold text-[14.5px] leading-snug">{c.t}</div>
              <p className="text-white/60 text-[13px] leading-relaxed mt-2">{c.d}</p>
            </div>
          ))}
        </div>

        {/* Machines autorisées */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-6 sm:p-8">
          <h2 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight mb-4">Quels véhicules pouvez-vous conduire ?</h2>
          <ul className="space-y-3">
            {machines.map((m, i) => (
              <li key={i} className="flex items-start gap-3 text-[13.5px]">
                <CheckCircle2 size={16} className="text-sky-400 shrink-0 mt-0.5" />
                <span className="text-white/75"><strong className="text-white">{m.k}</strong> — {m.v}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-500/25 bg-amber-500/5 p-4">
            <AlertTriangle size={16} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-white/70 text-[13px] leading-relaxed">
              La formation 7 h n'est valable <strong className="text-white">qu'en France</strong>. Pour conduire une 125 à l'étranger, le permis A1 reste nécessaire.
              Les <strong className="text-white">gants certifiés CE sont obligatoires</strong>, le casque homologué aussi — et un équipement complet (blouson, pantalon, chaussures montantes) est vivement recommandé.
            </p>
          </div>
        </div>

        {/* Dispenses */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0d0d0e] p-6 sm:p-8">
          <h2 className="font-serif-editorial text-white text-[20px] sm:text-[26px] leading-tight mb-4">Qui est dispensé de la formation ?</h2>
          <ul className="space-y-3">
            {dispenses.map((d, i) => (
              <li key={i} className="flex items-start gap-3 text-[13.5px]">
                <CheckCircle2 size={16} className="text-white/50 shrink-0 mt-0.5" />
                <span className="text-white/75">{d}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Aller plus loin : passerelle vers Code2Roues */}
        <div className="mt-8 relative overflow-hidden rounded-2xl border border-sky-500/25 bg-gradient-to-br from-[#081020] via-[#070b14] to-[#0d0d0e] p-6 sm:p-10" data-testid="permis-125-cta-c2r">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-sky-400/30 bg-sky-500/10 text-sky-300 text-[10.5px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-4">
            <Bike size={12} /> Notre plateforme sœur
          </div>
          <h2 className="font-serif-editorial text-white text-[22px] sm:text-[30px] leading-tight max-w-xl">
            Envie d'aller plus loin ? A1, A2, passerelle A : tout est sur Code2Roues.fr
          </h2>
          <p className="text-white/65 text-[13.5px] leading-relaxed mt-3 max-w-2xl">
            Trajectoire de sécurité, équipement, plateau interactif avec les 6 manœuvres officielles, 31 enquêtes magazine dédiées au 2 roues —
            la préparation moto complète, gratuite et sans pub, par la même équipe de moniteurs ECSR.
          </p>
          <div className="mt-6 flex flex-wrap gap-2 sm:gap-3">
            <a href={C2R} target="_blank" rel="noopener noreferrer" className="h-11 px-5 rounded-full bg-sky-500 hover:bg-sky-400 text-black text-[13.5px] font-semibold inline-flex items-center gap-2 transition-colors" data-testid="permis-125-open-c2r">
              Ouvrir Code2Roues.fr <ExternalLink size={14} />
            </a>
            <Link to="/nos-plateformes" className="h-11 px-5 rounded-full border border-white/15 text-white/85 hover:text-white text-[13.5px] font-medium hover:bg-white/5 inline-flex items-center gap-2 transition-colors" data-testid="permis-125-nos-plateformes">
              Découvrir nos plateformes <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Liens internes SEO */}
        <div className="mt-8 mb-4 flex flex-wrap gap-2 text-[12.5px]">
          {[
            { to: '/assr', label: 'ASSR & BSR : le hub scolarité' },
            { to: '/eleve', label: 'Préparer le permis B' },
            { to: '/magazine', label: 'Le magazine Code Voiture' },
          ].map((l, i) => (
            <Link key={i} to={l.to} className="px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-white/60 hover:text-white transition-colors">
              {l.label}
            </Link>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
